<template>
  <div class="page-wrapper">
    <page-header
      title="Custom UI Editor"
      :breadcrumb="breadcrumb"
      :container-class="containerClass"
      info-text="Customize the public homepage layout using Vue template code"
    />
    <div class="page-section">
      <div :class="containerClass">
        <b-card class="mb-3 border-left-primary">
          <div class="d-flex align-items-start">
            <div class="mr-3">
              <div class="bg-primary text-white rounded p-2">
                <i class="material-icons">code</i>
              </div>
            </div>
            <div>
              <h6 class="mb-2 font-weight-bold">How to use the Custom UI Editor</h6>
              <p class="mb-0 text-muted small">
                Edit the Vue template code below to customize your public homepage layout. Use the reference panel on the right to see all available properties, methods, and events.
              </p>
            </div>
          </div>
        </b-card>

        <b-alert variant="warning" show v-if="isSuperAdmin && !schoolId" class="d-flex align-items-center">
          <i class="material-icons mr-2">warning</i>
          <span>Please select a school from the previous screen to edit its custom UI.</span>
        </b-alert>

        <div v-if="isLoading" class="text-center py-5">
          <b-spinner variant="primary" class="mb-3" />
          <p class="text-muted mb-0">Loading custom layout data...</p>
        </div>

        <div v-else class="main-content">
          <div class="editor-container" :class="{ 'editor-fullscreen': isFullscreen }">
            <b-card no-body class="editor-card">
              <b-card-header class="bg-dark text-white py-2">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center">
                    <i class="material-icons icon-16pt text-warning mr-2">code</i>
                    <small class="font-weight-bold">custom_layout.vue</small>
                  </div>
                  <div class="d-flex align-items-center">
                    <b-badge :variant="hasUnsavedChanges ? 'warning' : 'success'" class="mr-2">
                      <span style="font-size: 8px;">●</span>
                    </b-badge>
                    <small class="mr-3">{{ hasUnsavedChanges ? 'Unsaved changes' : 'Saved' }}</small>
                    
                    <b-button
                      variant="link"
                      size="sm"
                      class="text-info p-0 mr-3"
                      @click="openPreviewInNewTab"
                      :disabled="!subdomainUrl"
                      v-b-tooltip.hover
                      title="Preview homepage in new tab"
                    >
                      <i class="material-icons icon-16pt">visibility</i>
                    </b-button>
                    
                    <!-- Action Buttons -->
                    <b-button
                      variant="link"
                      size="sm"
                      class="text-white p-0 mr-2"
                      @click="resetEditor"
                      :disabled="isSaving"
                      v-b-tooltip.hover
                      title="Reset to default state"
                    >
                      <i class="material-icons icon-16pt">refresh</i>
                    </b-button>
                    
                    <b-button
                      v-fmv-toggle.reference-sidebar
                      variant="link"
                      size="sm"
                      class="text-white p-0 mr-2"
                      v-b-tooltip.hover
                      title="Toggle reference panel"
                    >
                      <i class="material-icons icon-16pt">menu_book</i>
                    </b-button>
                    
                    <b-button
                      variant="link"
                      size="sm"
                      class="text-success p-0 mr-2"
                      @click="saveLayout"
                      :disabled="isSaving || (isSuperAdmin && !schoolId)"
                      v-b-tooltip.hover
                      title="Save changes"
                    >
                      <b-spinner small v-if="isSaving" variant="success" />
                      <i v-else class="material-icons icon-16pt">save</i>
                    </b-button>
                    
                    <b-button
                      variant="link"
                      size="sm"
                      class="text-white p-0"
                      @click="toggleFullscreen"
                      v-b-tooltip.hover
                      :title="isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
                    >
                      <i class="material-icons icon-16pt">{{ isFullscreen ? 'fullscreen_exit' : 'fullscreen' }}</i>
                    </b-button>
                  </div>
                </div>
              </b-card-header>
              <b-card-body class="p-0 editor-body">
                <codemirror
                  v-model="editorContent"
                  :options="cmOptions"
                  @input="onCodeChange"
                  class="code-mirror-wrapper"
                />
              </b-card-body>
            </b-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Page from '@/components/Page.vue';
import PageHeader from '@/components/Ui/PageHeader.vue';
import ReferenceSidebar from './ReferenceSidebar.vue';
import { USER_ROLES } from '@/common/constants';
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';
import { codemirror } from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/mode/htmlmixed/htmlmixed';
import 'codemirror/mode/javascript/javascript';
import 'codemirror/mode/css/css';

export default {
  name: 'HomepageCustomUiEditor',
  components: {
    PageHeader,
    codemirror,
  },
  extends: Page,
  data() {
    return {
      title: 'Custom UI Editor',
      isLoading: true,
      isSaving: false,
      linkedWebsite: null,
      editorContent: '{\n\n}',
      originalContent: '{\n\n}',
      defaultTemplateCode: '{\n\n}',
      enableCustomUi: true,
      schoolId: this.$route.query.school || null,
      sidebarVisible: false,
      isFullscreen: false,
      schoolData: null,
      cmOptions: {
        mode: 'htmlmixed', // Vue template mode
        lineNumbers: true,
        lineWrapping: true,
        theme: 'material',
        tabSize: 2,
        indentWithTabs: false,
        smartIndent: true,
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['getLoggedInUser']),
    breadcrumb() {
      return [
        { text: this.$t('home'), to: this.routes.home },
        {
          text: 'Website Configuration',
          to: { name: 'homepage-config' },
        },
        {
          text: 'Custom UI Editor',
          active: true,
        },
      ];
    },
    isSuperAdmin() {
      return get(this.getLoggedInUser, 'role') === USER_ROLES.SUPER_ADMIN;
    },
    hasUnsavedChanges() {
      return this.editorContent !== this.originalContent;
    },
    subdomainUrl() {
        return get(this.schoolData, 'school_linked_site.wbst_domain');
    },
    subLayout() {
      return true;
    },
    subLayoutDrawer() {
      return ReferenceSidebar;
    },
    subLayoutDrawerId() {
      return 'reference-sidebar';
    },
  },
  created() {
    this.loadCustomLayout();
  },
  methods: {
    ...mapActions('webConfig', ['getHomePageConfig', 'createHomepageConfig']),
    ...mapActions('school', ['getSchool']),
    async loadCustomLayout() {
      if (this.isSuperAdmin && !this.schoolId) {
        this.isLoading = false;
        return;
      }
      this.isLoading = true;
      try {
        const params = {};
        if (this.isSuperAdmin) {
          params.school_id = this.schoolId;
        }
        const resp = await this.getHomePageConfig(params);
        this.enableCustomUi = !!resp.custom_layout;
        this.linkedWebsite = resp.linked_website;
        // Load the code directly as string - no JSON serialization
        const savedCode = resp.custom_layout_data.custom_code || '';
        const defaultCode = resp.default_custom_code.custom_code;
        
        this.editorContent = savedCode;
        this.originalContent = savedCode; // This is what's currently saved in DB
        this.defaultTemplateCode = defaultCode; // This is the default template
        
        // Fetch school data for subdomain URL if super admin
        if (this.isSuperAdmin && this.schoolId) {
          const schoolResp = await this.getSchool(this.schoolId);
          this.schoolData = schoolResp.data;
        }
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: 'Failed to load custom UI data.' });
      }
      this.isLoading = false;
    },
    onCodeChange(newCode) {
      this.editorContent = newCode;
    },
    resetEditor() {
      this.editorContent = this.defaultTemplateCode;
      this.makeToast({ variant: 'info', msg: 'Editor reset to default state.' });
    },
    formatCode() {
      // Format code is handled by the editor itself
      // This is just a placeholder - users can use the editor's built-in formatting
      this.makeToast({ variant: 'info', msg: 'Use the editor\'s built-in formatting features.' });
    },
    async saveLayout() {
      // Save the code directly as string - no JSON parsing
      const payload = {
        custom_layout: this.enableCustomUi,
        custom_layout_data: {
          custom_code: this.editorContent
        },
      };

      if (this.isSuperAdmin && this.schoolId) {
        payload.linked_website = this.linkedWebsite;
        payload.school = this.schoolId;
      }

      this.isSaving = true;
      try {
        await this.createHomepageConfig(payload);
        this.originalContent = this.editorContent;
        this.makeToast({ variant: 'success', msg: 'Custom UI updated successfully.' });
      } catch (error) {
        this.makeToast({ variant: 'danger', msg: this.$t('generalMsgs.genErrorMsg') });
      }
      this.isSaving = false;
    },
    onCopySuccess() {
      this.makeToast({ variant: 'success', msg: 'Copied to clipboard!' });
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      // Refresh CodeMirror after fullscreen toggle
      this.$nextTick(() => {
        if (this.$refs.codemirror) {
          this.$refs.codemirror.codemirror.refresh();
        }
      });
    },
    openPreviewInNewTab() {
      if (this.subdomainUrl) {
        window.open(this.subdomainUrl, '_blank');
      }
    },
  },
};
</script>

<style scoped>
/* Page Layout - Viewport constrained */
.page-wrapper {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.page-section > div {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  padding-bottom: 1rem;
}

.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
}

/* Fullscreen Mode */
.editor-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: #fff;
  margin: 0 !important;
}

.editor-fullscreen .editor-card {
  border-radius: 0;
  height: 100vh;
}

@media (max-width: 991.98px) {
  .editor-container.sidebar-open {
    margin-right: 0;
  }
}

.editor-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.editor-body {
  background: #263238;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.code-mirror-wrapper {
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.code-mirror-wrapper >>> .CodeMirror {
  height: 100% !important;
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', Consolas, monospace;
  font-size: 13px;
  line-height: 1.6;
}

.code-mirror-wrapper >>> .CodeMirror-scroll {
  overflow-y: auto !important;
  overflow-x: auto !important;
}

</style>
